import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Box, Breadcrumbs, Typography } from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';
import { isEmpty } from 'lodash';
import Bluebird from 'bluebird';
import WeeklyHoursSelector from '../../../components/WeeklyHoursSelector';
import { convertApiDataToComponentFormat } from '../../../helpers/weeklyHoursConverter';
import {
  StyledRescuerBreadcrumb,
  StyledRescuerTextBreadcrumb,
} from '../../rescuerDashboard/rescuerDashboardMobile/Components/StyledRescuerBreadcrumb';
import routes from '../../../routes';
import { fetchReceiversIfNeeded, updateReceiverReceivingHours } from '../../../actions/receivers';
import ButtonWithLoading from '../../../components/ButtonWithLoading';
import snackbarHelper from '../../../helpers/snackbarHelper';
import { getUserRolesInCurrentlySelectedSite } from '../../../services/auth';
import useActiveUser from '../../../hooks/useActiveUser';
import { Roles } from '../../../models/roles';

const ReceiverMobileEditReceivingHoursBreadcrumb = () => {
  const { formatMessage } = useIntl();

  return (
    <Box boxShadow={0}>
      <Breadcrumbs aria-label="breadcrumb">
        <StyledRescuerBreadcrumb
          component={Link}
          to={generatePath(routes.mobileDashboard)}
          label={formatMessage({ id: 'breadcrumbs.home' })}
          icon={<HomeIcon style={{ color: '#fff' }} fontSize="small" />}
        />
        <StyledRescuerTextBreadcrumb
          component="a"
          href="#"
          label={formatMessage({ id: 'breadcrumbs.receiver-edit-receiving-hours' })}
        />
      </Breadcrumbs>
    </Box>
  );
};

const ReceiverMobileEditReceivingHours = () => {
  const activeUser = useActiveUser();
  const dispatch = useDispatch();
  const userRoles = getUserRolesInCurrentlySelectedSite(activeUser);
  const rolesWithAccess = userRoles.filter(
    (role) => role.role_name === Roles.ReceiverLocationAdmin || role.role_name === Roles.ReceiverLocationStaff
  );
  const receiverId = rolesWithAccess[0]?.receiver_id;
  const receivers = useSelector((state) => state.entities.receivers);
  const locations = receivers.locations.byReceiverId?.[receiverId] || [];
  const receiverLocation = locations[0];
  const [isLoading, setIsLoading] = useState();
  const [weeklyReceivingHours, setWeeklyReceivingHours] = useState({});
  const hoursInitialValues = convertApiDataToComponentFormat(receiverLocation?.weekly_receiving_hours);
  const hasError = !!Object.values(weeklyReceivingHours).find((day) => day.find((wrh) => wrh.error));

  useEffect(() => {
    dispatch(fetchReceiversIfNeeded());
  }, []);

  const submitHours = () => {
    setIsLoading(true);
    const data = { ...receiverLocation, weekly_receiving_hours: weeklyReceivingHours };

    Bluebird.try(() => dispatch(updateReceiverReceivingHours(receiverId, receiverLocation.id, data)))
      .then(() => snackbarHelper.success('Receiving Hours saved successfully!'))
      .catch((err) => snackbarHelper.error(err.message))
      .finally(() => {
        setIsLoading(false);
        setWeeklyReceivingHours({});
      });
  };

  if (!receiverId || receivers.inflight) {
    return 'Loading...';
  }

  return (
    <>
      <ReceiverMobileEditReceivingHoursBreadcrumb />

      <Box px={1} py={1}>
        <Typography variant="h6">Let us know when you can accept rescues!</Typography>
        <Box px={1} pt={1}>
          <WeeklyHoursSelector
            onChange={(hours) => {
              setWeeklyReceivingHours(hours);
            }}
            initialValues={hoursInitialValues}
            hideTitle
            hideBorder
          />
        </Box>

        <Box py={1}>
          <ButtonWithLoading
            onClick={submitHours}
            fullWidth
            color="primary"
            disabled={hasError || receivers.inflight || isEmpty(weeklyReceivingHours)}
            isLoading={isLoading}
          >
            Save
          </ButtonWithLoading>
        </Box>
      </Box>
    </>
  );
};

export default ReceiverMobileEditReceivingHours;
